/**
 * Gender Variant Picker — drives Size visibility for the new-shape gender model.
 *
 * Reads the `<script data-gender-variant-map>` JSON emitted by
 * snippets/product-variant-picker.liquid and:
 *   1. Hides Gender pills whose gender has zero available sizes.
 *   2. Hides the inline picker entirely if only one gender remains.
 *   3. On Gender change, hides Size pills whose (size, gender) combo isn't
 *      available, and switches the Size selection BEFORE the change event
 *      bubbles up to <variant-selects> so the recomputed variant is valid.
 *   4. Re-applies the filter after product-info.js swaps <variant-selects>
 *      via HTMLUpdateUtility.viewTransition (every variant change does this,
 *      which is why we need a MutationObserver instead of per-element bind).
 *
 * Old-shape products (separate Men's / Women's products linked by
 * gender_counterpart metafield) are handled by gender-toggle.js instead.
 */
(function () {
  if (window.__genderVariantPickerLoaded) return;
  window.__genderVariantPickerLoaded = true;

  function buildAvailabilityMap(data) {
    const sizeIdx = data.size_position - 1;
    const genderIdx = data.gender_position - 1;
    var available = {};
    var all = {};
    data.variants.forEach(function (v) {
      const size = v.options[sizeIdx];
      const gender = v.options[genderIdx];
      if (!available[gender]) available[gender] = new Set();
      if (!all[gender]) all[gender] = new Set();
      all[gender].add(size);
      if (v.available) available[gender].add(size);
    });
    return { byGender: available, allByGender: all, sizeName: data.size_name || 'Size', genderName: data.gender_name || 'Gender' };
  }

  function readMap(root) {
    const node = root.querySelector('script[data-gender-variant-map]');
    if (!node) return null;
    try {
      return buildAvailabilityMap(JSON.parse(node.textContent));
    } catch (e) {
      return null;
    }
  }

  function getSelectedGender(root) {
    const inputs = root.querySelectorAll('.gender-toggle-inline__input');
    for (let i = 0; i < inputs.length; i++) {
      if (inputs[i].checked) return inputs[i].value;
    }
    return null;
  }

  function applyGenderFilter(root, map) {
    const selectedGender = getSelectedGender(root);
    if (!selectedGender) return;

    const allSizes = map.allByGender[selectedGender] || new Set();
    const available = map.byGender[selectedGender] || new Set();
    const sizeNamePrefix = map.sizeName + '-';
    const sizeInputs = Array.prototype.slice.call(
      root.querySelectorAll('input[type="radio"][name^="' + cssEscape(sizeNamePrefix) + '"]')
    );

    // Resolve a valid Size selection BEFORE hiding the previously-selected one.
    // Prefer an in-stock size; fall back to any size that exists for this gender.
    let currentSize = null;
    sizeInputs.forEach(function (input) {
      if (input.checked) currentSize = input;
    });
    if (!currentSize || !allSizes.has(currentSize.value)) {
      var picked = false;
      for (let i = 0; i < sizeInputs.length; i++) {
        if (available.has(sizeInputs[i].value)) {
          sizeInputs[i].checked = true;
          picked = true;
          break;
        }
      }
      if (!picked) {
        for (let i = 0; i < sizeInputs.length; i++) {
          if (allSizes.has(sizeInputs[i].value)) {
            sizeInputs[i].checked = true;
            break;
          }
        }
      }
    }

    sizeInputs.forEach(function (input) {
      const label = root.querySelector('label[for="' + input.id + '"]');
      const exists = allSizes.has(input.value);
      if (exists) {
        input.removeAttribute('hidden');
        if (label) label.removeAttribute('hidden');
      } else {
        input.setAttribute('hidden', '');
        if (label) label.setAttribute('hidden', '');
        if (input.checked) input.checked = false;
      }
    });

    applyStickyFilter(map, selectedGender, allSizes);
  }

  function applyStickyFilter(map, selectedGender, available) {
    const sizeName = map.sizeName;

    // Sticky desktop pill group for Size
    document
      .querySelectorAll('[data-sticky-atc-pills-option="' + cssEscapeAttr(sizeName) + '"] [data-sticky-atc-pill-value]')
      .forEach(function (pill) {
        const visible = available.has(pill.dataset.stickyAtcPillValue);
        if (visible) {
          pill.removeAttribute('hidden');
          pill.removeAttribute('aria-hidden');
        } else {
          pill.setAttribute('hidden', '');
          pill.setAttribute('aria-hidden', 'true');
          pill.classList.remove('is-selected');
        }
      });

    // Sticky mobile <select> for Size
    document
      .querySelectorAll('[data-sticky-atc-option="' + cssEscapeAttr(sizeName) + '"]')
      .forEach(function (select) {
        let needsReselect = false;
        Array.prototype.slice.call(select.options).forEach(function (opt) {
          const visible = available.has(opt.value);
          if (visible) {
            opt.removeAttribute('hidden');
            opt.disabled = false;
          } else {
            opt.setAttribute('hidden', '');
            opt.disabled = true;
            if (opt.selected) needsReselect = true;
          }
        });
        if (needsReselect) {
          const firstAvail = Array.prototype.slice.call(select.options).find(function (o) {
            return !o.hidden;
          });
          if (firstAvail) {
            select.value = firstAvail.value;
            select.dispatchEvent(new Event('change', { bubbles: true }));
          }
        }
      });

    // Sticky gender pill group: mark currently-selected gender pill.
    const genderName = map.genderName;
    document
      .querySelectorAll('[data-sticky-atc-pills-option="' + cssEscapeAttr(genderName) + '"] [data-sticky-atc-pill-value]')
      .forEach(function (pill) {
        const selected = pill.dataset.stickyAtcPillValue === selectedGender;
        pill.classList.toggle('is-selected', selected);
        pill.setAttribute('aria-pressed', selected ? 'true' : 'false');
      });
  }

  // Minimal attribute-value escaper for use inside querySelector strings.
  // CSS.escape exists in modern browsers; fall back to a basic strip for safety.
  function cssEscape(s) {
    if (window.CSS && typeof window.CSS.escape === 'function') return window.CSS.escape(s);
    return String(s).replace(/(["\\])/g, '\\$1');
  }
  function cssEscapeAttr(s) {
    return String(s).replace(/(["\\])/g, '\\$1');
  }

  function hideEmptyGenderPills(root, map) {
    const inputs = root.querySelectorAll('.gender-toggle-inline__input');
    let visibleCount = 0;
    let firstVisible = null;

    inputs.forEach(function (input) {
      const label = root.querySelector('label[for="' + input.id + '"]');
      var allSizes = map.allByGender[input.value];
      var hasVariants = allSizes && allSizes.size > 0;
      if (!hasVariants) {
        input.setAttribute('hidden', '');
        if (label) label.setAttribute('hidden', '');
        if (input.checked) input.checked = false;
      } else {
        input.removeAttribute('hidden');
        if (label) label.removeAttribute('hidden');
        visibleCount += 1;
        if (!firstVisible) firstVisible = input;
      }
    });

    const stillChecked = Array.prototype.some.call(inputs, function (i) {
      return i.checked;
    });
    if (!stillChecked && firstVisible) {
      firstVisible.checked = true;
    }

    const container = root.querySelector('.gender-toggle-inline');
    if (container) {
      const collapse = visibleCount <= 1;
      if (collapse) {
        container.setAttribute('hidden', '');
      } else {
        container.removeAttribute('hidden');
      }
      const separator = container.previousElementSibling;
      if (separator && separator.classList.contains('gender-toggle-inline__separator')) {
        if (collapse) {
          separator.setAttribute('hidden', '');
        } else {
          separator.removeAttribute('hidden');
        }
      }
    }
  }

  function refreshRoot(root) {
    const map = readMap(root);
    if (!map) return;
    hideEmptyGenderPills(root, map);
    applyGenderFilter(root, map);
  }

  function refreshAll() {
    document.querySelectorAll('variant-selects').forEach(refreshRoot);
  }

  // Document-level capture-phase change handler. Survives variant-selects
  // DOM swaps because we never bind to a specific root. Capture phase fires
  // BEFORE variant-selects' bubbling listener, so the Size DOM is corrected
  // by the time variant-selects recomputes.
  document.addEventListener('change', function (e) {
    if (!e.target || !e.target.matches || !e.target.matches('.gender-toggle-inline__input')) return;
    const root = e.target.closest('variant-selects');
    if (!root) return;
    const map = readMap(root);
    if (map) applyGenderFilter(root, map);
  }, true);

  // product-info.js swaps the entire <variant-selects> element on every
  // variant change via HTMLUpdateUtility.viewTransition. The new element
  // arrives with all sizes visible (Shopify renders them disabled-but-shown).
  // Re-apply the filter whenever a variant-selects shows up in the DOM.
  const observer = new MutationObserver(function (mutations) {
    let touched = false;
    for (let i = 0; i < mutations.length && !touched; i++) {
      const m = mutations[i];
      for (let j = 0; j < m.addedNodes.length; j++) {
        const node = m.addedNodes[j];
        if (node.nodeType !== 1) continue;
        const isVS = node.tagName && node.tagName.toLowerCase() === 'variant-selects';
        const hasVS = node.querySelector && node.querySelector('variant-selects');
        if (isVS || hasVS) {
          touched = true;
          break;
        }
      }
    }
    if (touched) refreshAll();
  });

  function startObserver() {
    if (document.body) {
      observer.observe(document.body, { childList: true, subtree: true });
      refreshAll();
    } else {
      document.addEventListener('DOMContentLoaded', startObserver);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', startObserver);
  } else {
    startObserver();
  }
})();
